/**
 * Date formatting helpers per DESIGN-RULES Rule 19.
 * All functions take ISO strings or Date objects and return formatted strings.
 */

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

/** Long English weekday names indexed by day-of-week (0=Sun..6=Sat). */
export const WEEKDAY_NAMES_LONG = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const;

function toDate(input: string | Date): Date {
  return typeof input === 'string' ? new Date(input) : input;
}

/** MMM YYYY — e.g. "Mar 2026" */
export function formatMonth(input: string | Date): string {
  const d = toDate(input);
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

/** MMM D, YYYY — e.g. "Jan 15, 2026" */
export function formatDayFull(input: string | Date): string {
  const d = toDate(input);
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()}`;
}

/** MMM D — e.g. "Mar 2" */
export function formatDayShort(input: string | Date): string {
  const d = toDate(input);
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}`;
}

/** MMM D, Ddd — e.g. "Mar 28, Fri" */
export function formatDayWithName(input: string | Date): string {
  const d = toDate(input);
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}, ${DAYS[d.getUTCDay()]}`;
}

/** MMM D HH:MM — e.g. "Apr 13 17:00". Used by the sync-log table where
 *  absolute timestamps read better than relative "3h ago" strings. */
export function formatDateTimeShort(input: string | Date): string {
  const d = toDate(input);
  const hh = String(d.getUTCHours()).padStart(2, '0');
  const mm = String(d.getUTCMinutes()).padStart(2, '0');
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()} ${hh}:${mm}`;
}

/** Current wall-clock instant. Isolated here so the "no raw Date" rule
 *  can stay enforced elsewhere in src/; callers that need a live clock
 *  (relative-preset resolution, etc.) go through this. */
export function currentDate(): Date {
  return new Date();
}

/**
 * Coarse "X ago" formatter — "5 minutes ago", "2 hours ago", "3 days ago".
 * Locale-aware via Intl.RelativeTimeFormat. Used by the Settings → Jira
 * Integration page's "Last synced" column (task 137 §D7).
 */
export function formatRelativeShort(input: string | Date): string {
  const then = toDate(input).getTime();
  const now = currentDate().getTime();
  const diffSeconds = Math.round((then - now) / 1000);
  const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
  const abs = Math.abs(diffSeconds);
  if (abs < 60) return rtf.format(diffSeconds, 'second');
  if (abs < 3600) return rtf.format(Math.round(diffSeconds / 60), 'minute');
  if (abs < 86_400) return rtf.format(Math.round(diffSeconds / 3600), 'hour');
  return rtf.format(Math.round(diffSeconds / 86_400), 'day');
}

/**
 * Format an ISO UTC timestamp as "H:MM AM/PM" in the given IANA timezone.
 * Example: `formatTimeInTz('2026-04-23T14:15:00.000Z', 'America/New_York')`
 * → `"10:15 AM"`. Used by the Timesheet Daily page (task 129 D3) to format
 * firstEvent / lastEvent through the same timezone-aware formatter that
 * the Range page uses for block positioning — keeps the bar and the stat
 * cards consistent to the minute.
 */
export function formatTimeInTz(iso: string, tz: string): string {
  const d = new Date(iso);
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).formatToParts(d);
  let h = '';
  let m = '';
  let period = '';
  for (const p of parts) {
    if (p.type === 'hour') h = p.value;
    else if (p.type === 'minute') m = p.value;
    else if (p.type === 'dayPeriod') period = p.value.toUpperCase();
  }
  return `${h}:${m} ${period}`;
}

/**
 * Format an ISO UTC timestamp as "MMM D, h:MM AM/PM" in the given IANA
 * timezone. Used by the Activity Log → Events drill-down page (task 169)
 * to render per-event timestamps in the org timezone, since the timestamp
 * column needs both date and time on a single line.
 */
export function formatDateTimeInTz(iso: string, tz: string): string {
  const d = new Date(iso);
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).formatToParts(d);
  return parts
    .map((p) => (p.type === 'dayPeriod' ? p.value.toUpperCase() : p.value))
    .join('')
    .trim();
}

/**
 * Day-of-week (0=Sun..6=Sat) for a YYYY-MM-DD date string, interpreted
 * in the given IANA timezone. Avoids the subtle bug of `new Date('YYYY-MM-DD')
 * .getUTCDay()` returning the wrong day when the org timezone is west of UTC
 * and the date string represents a local calendar date.
 */
export function dayOfWeekInTz(dateYmd: string, tz: string): number {
  // Anchor at 12:00 UTC on the given civil date to avoid DST edges.
  const d = new Date(`${dateYmd}T12:00:00.000Z`);
  const fmt = new Intl.DateTimeFormat('en-US', { timeZone: tz, weekday: 'short' });
  const wk = fmt.format(d);
  const DAY_INDEX: Record<string, number> = {
    Sun: 0,
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6,
  };
  return DAY_INDEX[wk] ?? 0;
}

/** Format a duration in minutes as "Xh Ym", "Xh", or "Ym". */
export function formatDurationMinutes(totalMinutes: number): string {
  const m = Math.round(totalMinutes);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem === 0 ? `${h}h` : `${h}h ${rem}m`;
}

/**
 * Add (or subtract) a number of days to a YYYY-MM-DD civil date and
 * return the shifted YYYY-MM-DD. Arithmetic is in UTC to sidestep DST
 * boundaries — the caller reads this as a civil date, not an instant.
 */
export function addDaysYmd(dateYmd: string, days: number): string {
  const d = new Date(`${dateYmd}T00:00:00.000Z`);
  const shifted = new Date(d.getTime() + days * 24 * 60 * 60 * 1000);
  return shifted.toISOString().slice(0, 10);
}
